import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronRight, Download, FileText, Folder, FolderPlus, HardDrive, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, Empty, PageHeader, Spinner } from "@/components/Field";
import { StatusBadge } from "@/components/StatusBadge";
import { api, download, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";

const fmtSize = (b) => (b == null ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

const Crumbs = ({ crumbs, onOpen }) => (
  <nav data-testid="documents-breadcrumb" className="flex flex-wrap items-center gap-1 text-sm">
    {crumbs.map((c, i) => (
      <span key={c.id} className="flex items-center gap-1">
        {i > 0 && <ChevronRight className="h-4 w-4 text-slate-400" />}
        <button data-testid={`documents-crumb-${i}`} onClick={() => onOpen(c.id)} className={i === crumbs.length - 1 ? "font-semibold text-slate-900" : "text-primary hover:underline"}>{c.name}</button>
      </span>
    ))}
  </nav>
);

const DocRow = ({ f, onOpen, onDelete, canDelete }) => (
  <li data-testid={`document-row-${f.id}`} className="flex items-center gap-3 px-5 py-3">
    {f.is_folder ? <Folder className="h-5 w-5 text-amber-500 shrink-0" /> : <FileText className="h-5 w-5 text-slate-400 shrink-0" />}
    <button data-testid={`document-open-${f.id}`} onClick={() => onOpen(f)} className="min-w-0 flex-1 text-left">
      <div className="text-[15px] font-medium text-slate-900 truncate">{f.name}{f.is_google && " (PDF)"}</div>
      <div className="text-xs text-slate-500">{[!f.is_folder && fmtSize(f.size ? Number(f.size) : null), fmtDateTime(f.modifiedTime)].filter(Boolean).join(" · ")}</div>
    </button>
    {!f.is_folder && <Button data-testid={`document-download-${f.id}`} variant="ghost" size="icon" className="h-10 w-10" onClick={() => onOpen(f)} aria-label="Scarica"><Download className="h-4 w-4" /></Button>}
    {canDelete && <Button data-testid={`document-delete-${f.id}`} variant="ghost" size="icon" className="h-10 w-10 text-red-700" onClick={() => onDelete(f)} aria-label="Elimina"><Trash2 className="h-4 w-4" /></Button>}
  </li>
);

export default function DocumentsPage() {
  const { can } = useAuth();
  const [folder, setFolder] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/documents", { params: folder ? { folder } : {} }).then((r) => setData(r.data)).catch((e) => { toast.error(formatError(e)); setData({ connected: true, items: [], breadcrumb: [], error: true }); });
  }, [folder]);
  useEffect(() => { load(); }, [load]);

  const open = (f) => (f.is_folder ? setFolder(f.id) : download(`/documents/${f.id}/download`, f.is_google ? `${f.name}.pdf` : f.name).catch((e) => toast.error(formatError(e))));
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const upload = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (files.length) act(async () => { for (const file of files) { const fd = new FormData(); fd.append("file", file); if (data.folder_id) fd.append("folder", data.folder_id); await api.post("/documents/upload", fd); } }, files.length > 1 ? "File caricati" : "File caricato");
  };
  const newFolder = () => {
    const name = window.prompt("Nome della nuova cartella");
    if (name?.trim()) act(() => { const fd = new FormData(); fd.append("name", name.trim()); if (data.folder_id) fd.append("parent", data.folder_id); return api.post("/documents/folder", fd); }, "Cartella creata");
  };
  const remove = (f) => window.confirm(`Eliminare «${f.name}»?`) && act(() => api.delete(`/documents/${f.id}`), "Eliminato");

  return (
    <>
      <PageHeader title="Documenti" subtitle="Cartella condivisa dell'associazione su Google Drive">
        {data?.connected && can("documents", "create") && (
          <>
            <Button data-testid="documents-new-folder" variant="outline" className="h-11" disabled={busy} onClick={newFolder}><FolderPlus className="h-4 w-4 mr-1" /> Nuova cartella</Button>
            <Button data-testid="documents-upload" className="h-11" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? <Spinner className="h-4 w-4" /> : <><Upload className="h-4 w-4 mr-1" /> Carica file</>}</Button>
            <input ref={fileRef} type="file" multiple hidden data-testid="documents-file-input" onChange={upload} />
          </>
        )}
      </PageHeader>
      {!data ? <Spinner /> : !data.connected ? (
        <Card testId="documents-not-connected" className="p-6 flex items-center gap-3"><HardDrive className="h-6 w-6 text-slate-400" /><div><StatusBadge label="Google Drive non collegato" /><p className="text-sm text-slate-500 mt-1">Un amministratore può collegarlo da Impostazioni.</p></div></Card>
      ) : (
        <Card testId="documents-list">
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
            <Crumbs crumbs={data.breadcrumb} onOpen={setFolder} />
            <StatusBadge label="Google Drive collegato" tone="success" />
          </div>
          {data.items.length === 0 ? <Empty text="Questa cartella è vuota." testId="documents-empty" /> : (
            <ul className="divide-y divide-slate-100">{data.items.map((f) => <DocRow key={f.id} f={f} onOpen={open} onDelete={remove} canDelete={can("documents", "delete")} />)}</ul>
          )}
        </Card>
      )}
    </>
  );
}
